/** @jsx jsx */
import { jsx } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useState } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";

import Button from "../Button";
import InputElementWrapper from "../Input/InputElementWrapper";
import InputField from "../Input/InputField";

import { isValidEmail } from "../../utils";
import { media, Breakpoint } from "../../theme";

export const SubscribeFormInner = styled.form<{ hasError: boolean }>`
  max-width: 440px;
  height: 120px;

  ${media(Breakpoint.Tablet)} {
    height: 50px;
  }

  ${InputElementWrapper} {
    border-color: ${({ hasError, theme }) => (hasError ? theme.color.red : theme.color.white)};

    ${media(Breakpoint.Tablet)} {
      width: 325px;
    }
  }

  ${Button}[type="submit"] {
    width: 100%;
    height: 50px;
    margin-top: 20px;

    ${media(Breakpoint.Tablet)} {
      border-left: 0;
      margin: 0;
    }
  }

  ${media(Breakpoint.Tablet)} {
    display: flex;
  }
`;

interface SubscribeFormProps {
  className?: string;
  onSubmit?: React.FormEventHandler;
  onSuccess?: () => void;
}

function SubscribeForm({ className, onSubmit, onSuccess }: SubscribeFormProps) {
  const [formError, setFormError] = useState<string>("");
  const [formState, setFormState] = useState<"default" | "submitting" | "submitted">("default");
  const [email, setEmail] = useState<string>("");
  const { executeRecaptcha } = useGoogleReCaptcha();

  const onSubmitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!executeRecaptcha) {
      setFormError("reCAPTCHA not loaded. Please try again.");
      return false;
    }

    if (isValidEmail(email)) {
      setFormError("");
      setFormState("submitting");

      try {
        // Execute reCAPTCHA to get token
        const recaptchaToken = await executeRecaptcha("subscribe");

        const response = await fetch("/.netlify/functions/subscribe", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, recaptchaToken }),
        });

        const data = await response.json();
        console.log("res", data);

        if (data.success) {
          setFormState("submitted");
          onSuccess?.();
        } else {
          setFormError(data.error || "Subscription failed. Please try again.");
          setFormState("default");
        }
      } catch (error) {
        console.error("Subscription error:", error);
        setFormError("An error occurred. Please try again.");
        setFormState("default");
      }
    } else {
      setFormError("Invalid Email");
      setFormState("default");
    }
    return false;
  };

  let formContent: JSX.Element;
  if (formState === "submitting" || formState === "submitted") {
    formContent = (
      <Button
        css={{
          width: "100%",
          height: "100%",
          backgroundColor: "transparent !important",
          cursor: "default !important",
        }}
        disabled
        variant="transparent"
      >
        {formState === "submitting" ? "Please Wait..." : "Thank You!"}
      </Button>
    );
  } else {
    formContent = (
      <React.Fragment>
        <InputField
          ariaLabel="Email"
          error={formError}
          placeholder="EMAIL ADDRESS"
          onKeyPressEvent={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
              onSubmit?.(e);
            }
          }}
          id="emailaddr_"
          name="emailaddr_"
          type="email"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(event.currentTarget.value);
          }}
        />
        <Button type="submit" variant="transparent">
          Submit
        </Button>
      </React.Fragment>
    );
  }

  return (
    <div className={className}>
      <SubscribeFormInner hasError={!!formError} onSubmit={onSubmitForm} css={{ margin: "0 auto" }}>
        {formContent}
      </SubscribeFormInner>
    </div>
  );
}

const StyledSubscribeForm = styled(SubscribeForm)`
  position: relative;
  width: 100%;
`;

// Wrapper component with reCAPTCHA provider
interface SubscribeFormWithRecaptchaProps {
  className?: string;
  onSubmit?: React.FormEventHandler;
  onSuccess?: () => void;
}

const SubscribeFormWithRecaptcha = (props: SubscribeFormWithRecaptchaProps) => {
  const recaptchaSiteKey = process.env.GATSBY_RECAPTCHA_SITE_KEY;

  if (!recaptchaSiteKey || recaptchaSiteKey === "your_recaptcha_site_key_here") {
    console.error(
      "GATSBY_RECAPTCHA_SITE_KEY is not set or still using placeholder value. " +
      "Please update .env.development with your actual reCAPTCHA site key."
    );
    // Return a div with error message in development
    if (process.env.NODE_ENV === "development") {
      return (
        <div style={{ color: "red", padding: "20px", border: "1px solid red" }}>
          <strong>Development Error:</strong> GATSBY_RECAPTCHA_SITE_KEY is not configured.
          <br />
          Please update <code>.env.development</code> with your reCAPTCHA keys.
        </div>
      );
    }
    // In production, return the form without reCAPTCHA (fallback)
    return <StyledSubscribeForm {...props} />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <StyledSubscribeForm {...props} />
    </GoogleReCaptchaProvider>
  );
};

export default SubscribeFormWithRecaptcha;
